import type {
  AvailabilityDay,
  AvailabilitySettingsRequest,
  AvailabilitySettingsResponse,
  AvailabilityWeekday,
  AvailabilityWindow,
} from './availabilityTypes';

export const AVAILABILITY_WEEKDAYS: AvailabilityWeekday[] = [1, 2, 3, 4, 5, 6, 7];

export const WEEKDAY_LABELS: Record<AvailabilityWeekday, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export type EditableMinuteValue = number | '';

export type AvailabilityScheduleKind = 'coaching' | 'chemistry';

export interface EditableAvailabilitySettings {
  time_zone: string;
  booking_interval_minutes: EditableMinuteValue;
  buffer_before_minutes: EditableMinuteValue;
  buffer_after_minutes: EditableMinuteValue;
  minimum_notice_minutes: EditableMinuteValue;
  days: EditableAvailabilityDay[];
  chemistry_days: EditableAvailabilityDay[];
}

export interface EditableAvailabilityDay {
  weekday: AvailabilityWeekday;
  enabled: boolean;
  windows: EditableAvailabilityWindow[];
}

export interface EditableAvailabilityWindow extends AvailabilityWindow {
  id: string;
}

export function toEditableAvailability(
  response: AvailabilitySettingsResponse
): EditableAvailabilitySettings {
  return {
    time_zone: response.time_zone,
    booking_interval_minutes: response.booking_interval_minutes,
    buffer_before_minutes: response.buffer_before_minutes,
    buffer_after_minutes: response.buffer_after_minutes,
    minimum_notice_minutes: response.minimum_notice_minutes,
    days: toEditableDays(response.days),
    chemistry_days: toEditableDays(response.chemistry_days ?? response.days),
  };
}

// The full editor state in API shape, chemistry_days included. The panel keeps
// these snapshots to dedupe autosaves and to rebase after a conflict.
export function toAvailabilitySettingsSnapshot(
  settings: EditableAvailabilitySettings
): AvailabilitySettingsResponse {
  return {
    time_zone: settings.time_zone.trim(),
    booking_interval_minutes: Number(settings.booking_interval_minutes),
    buffer_before_minutes: Number(settings.buffer_before_minutes),
    buffer_after_minutes: Number(settings.buffer_after_minutes),
    minimum_notice_minutes: Number(settings.minimum_notice_minutes),
    days: toAvailabilityDaysRequest(settings.days),
    chemistry_days: toAvailabilityDaysRequest(settings.chemistry_days),
  };
}

// What the editor knows about the server's Chem Call hours.
export interface ChemistrySync {
  // The Chem Call hours the server is known to hold.
  baseline: AvailabilityDay[];
  // True once a request carrying chemistry_days was sent and the server's
  // answer has not yet become the baseline. The server may then hold hours
  // other than `baseline`, so the next request must carry the editor's hours.
  sentSinceBaseline: boolean;
}

// The PUT body. It omits chemistry_days only when the editor's Chem Call hours
// equal the synced baseline and no chem request is unconfirmed. Omitting the
// field keeps the stored Chem Call hours, so a coach who never set them keeps
// following the Coaching hours, and an older server's response without
// chemistry_days cannot be written back as an explicit list.
export function toAvailabilitySettingsRequest(
  settings: EditableAvailabilitySettings,
  chemistrySync: ChemistrySync
): AvailabilitySettingsRequest {
  const { chemistry_days: chemistryDays = [], ...request } =
    toAvailabilitySettingsSnapshot(settings);
  return !chemistrySync.sentSinceBaseline &&
    availabilityDaysEqual(chemistryDays, chemistrySync.baseline)
    ? request
    : { ...request, chemistry_days: chemistryDays };
}

// Compares windows by time only, ignoring ids and order.
export function availabilityWindowsEqual(
  left: AvailabilityWindow[],
  right: AvailabilityWindow[]
): boolean {
  const canonicalize = (windows: AvailabilityWindow[]) =>
    windows.map(({ start_time, end_time }) => `${start_time.trim()}-${end_time.trim()}`).sort();
  const canonicalLeft = canonicalize(left);
  const canonicalRight = canonicalize(right);
  return (
    canonicalLeft.length === canonicalRight.length &&
    canonicalLeft.every((window, index) => window === canonicalRight[index])
  );
}

// Compares weekly hours by weekday, ignoring window ids and order and the
// window lists of disabled days. A missing weekday counts as disabled.
export function availabilityDaysEqual(left: AvailabilityDay[], right: AvailabilityDay[]): boolean {
  const leftByWeekday = new Map(left.map((day) => [day.weekday, day]));
  const rightByWeekday = new Map(right.map((day) => [day.weekday, day]));
  return AVAILABILITY_WEEKDAYS.every((weekday) => {
    const leftDay = leftByWeekday.get(weekday);
    const rightDay = rightByWeekday.get(weekday);
    const leftEnabled = leftDay?.enabled ?? false;
    if (leftEnabled !== (rightDay?.enabled ?? false)) {
      return false;
    }
    return (
      !leftEnabled || availabilityWindowsEqual(leftDay?.windows ?? [], rightDay?.windows ?? [])
    );
  });
}

export function daysKeyFor(kind: AvailabilityScheduleKind): 'days' | 'chemistry_days' {
  return kind === 'chemistry' ? 'chemistry_days' : 'days';
}

// Runs a day updater written against `days` on the list for `kind`, leaving the
// other list untouched.
export function withActiveDays(
  settings: EditableAvailabilitySettings,
  kind: AvailabilityScheduleKind,
  updater: (asCoaching: EditableAvailabilitySettings) => EditableAvailabilitySettings
): EditableAvailabilitySettings {
  const key = daysKeyFor(kind);
  const updated = updater({ ...settings, days: settings[key] });
  return { ...settings, [key]: updated.days };
}

export function copyAvailabilityDay(
  settings: EditableAvailabilitySettings,
  sourceWeekday: AvailabilityWeekday,
  targetWeekdays: AvailabilityWeekday[]
): EditableAvailabilitySettings {
  const sourceDay = settings.days.find((day) => day.weekday === sourceWeekday);
  if (!sourceDay || targetWeekdays.length === 0) {
    return settings;
  }
  const targetSet = new Set(targetWeekdays.filter((weekday) => weekday !== sourceWeekday));
  return {
    ...settings,
    days: settings.days.map((day) => {
      if (!targetSet.has(day.weekday)) {
        return day;
      }
      return {
        weekday: day.weekday,
        enabled: sourceDay.enabled,
        windows: sourceDay.enabled
          ? sourceDay.windows.map((window, index) => ({
              ...window,
              id: buildWindowID(day.weekday, window, index),
            }))
          : [],
      };
    }),
  };
}

export function withDayEnabled(
  settings: EditableAvailabilitySettings,
  weekday: AvailabilityWeekday,
  enabled: boolean
): EditableAvailabilitySettings {
  return {
    ...settings,
    days: settings.days.map((day) => {
      if (day.weekday !== weekday) {
        return day;
      }
      return {
        ...day,
        enabled,
        windows: enabled
          ? day.windows.length === 0
            ? [defaultEditableWindow(weekday, 0)]
            : day.windows
          : [],
      };
    }),
  };
}

function toEditableDays(days: AvailabilityDay[]): EditableAvailabilityDay[] {
  const daysByWeekday = new Map(days.map((day) => [day.weekday, day]));
  return AVAILABILITY_WEEKDAYS.map((weekday) => toEditableDay(weekday, daysByWeekday.get(weekday)));
}

function toAvailabilityDaysRequest(days: EditableAvailabilityDay[]): AvailabilityDay[] {
  return days
    .slice()
    .sort((left, right) => left.weekday - right.weekday)
    .map(toAvailabilityDayRequest);
}

function toEditableDay(
  weekday: AvailabilityWeekday,
  day?: AvailabilityDay
): EditableAvailabilityDay {
  if (!day) {
    return { weekday, enabled: false, windows: [] };
  }
  return {
    weekday,
    enabled: day.enabled,
    windows: day.windows.map((window, index) => ({
      ...window,
      id: window.id || buildWindowID(weekday, window, index),
    })),
  };
}

function toAvailabilityDayRequest(day: EditableAvailabilityDay): AvailabilityDay {
  return {
    weekday: day.weekday,
    enabled: day.enabled,
    windows: day.enabled
      ? day.windows.map((window) => ({
          id: window.id,
          start_time: window.start_time.trim(),
          end_time: window.end_time.trim(),
        }))
      : [],
  };
}

function defaultEditableWindow(
  weekday: AvailabilityWeekday,
  index: number
): EditableAvailabilityWindow {
  const window = { start_time: '09:00', end_time: '17:00' };
  return { ...window, id: buildWindowID(weekday, window, index) };
}

function buildWindowID(
  weekday: AvailabilityWeekday,
  window: Pick<AvailabilityWindow, 'start_time' | 'end_time'>,
  index: number
): string {
  return `${weekday}-${index}-${window.start_time}-${window.end_time}`;
}
